import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import produce, { Draft } from 'immer';
import { FileRequest, FileRequestTarget, FileRequestStatus, FetchFileRequestFilters, FileRequestAction, RobotCloudAPI, isDownloadFileRequest,
    RobotCommunicationType } from '@moderepo/mode-apis';
import { BaseCompAction, capitalizeAll, capitalizeFirst, ITEMS_PER_PAGE_100, ITEMS_PER_PAGE_LIST, PaginationBar, PAGING_MIN_PAGES, SelectInputOption,
    Time, useSearchParams, getErrorMessage, GenericMessageBox } from '@moderepo/mode-app-components';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchAllFileRequests, fetchFileRequestById, fetchRobotById, rcDataContext, selectAllFileRequests, selectRobotById }
    from '@moderepo/mode-data-state-robot-cloud';
import { modeUIContext, showErrorNotification, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { Agent, ApiError, Project } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../../containerUtils';
import * as RCUtils from '../RCUtils';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { FileRequestUserAction, RCFileRequestsTable, RCFileRequestsTableDataItem, RCFileRequestsTableFilters, RCFileRequestsTableProps }
    from '../../../components/robot-cloud';
import { RCFileRequestPreviewCompOptions, RCSlideOutPanelCompType } from '../RCProjectConfig';
import { ProjectTypeHandlersManager } from '../../../utils/ritzExtensionUtils';
import { useSelectRobotDefinitionById as useSelectRobotDefinition } from '../rcDataStateHooks';



/**
 * Create an Array of FileRequestTarget option used for filter by file request target dropdown
 * @param trans
 */
const createFilterByTargetOptions = (trans: TFunction): readonly SelectInputOption<FileRequestTarget | ''>[] => {
    return [{
        label: capitalizeAll(trans('label.show_all')),
        value: '',
    }, ...(Object.values(FileRequestTarget).map((targetType: FileRequestTarget): SelectInputOption<FileRequestTarget> => {
        return {
            label: capitalizeAll(trans(RCUtils.getFileRequestTargetName(targetType))),
            value: targetType,
        };
    }))];
};



/**
 * Create an Array of FileRequestTarget option used for filter by file request target dropdown
 * @param trans
 */
const createFilterByActionOptions = (
    trans: TFunction, project: Project | undefined, loggedInAgent: Agent | undefined,
): readonly SelectInputOption<FileRequestAction | ''>[] => {

    const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
        project?.supportToolSettings.projectType,
    )?.getCustomUIConfig(loggedInAgent, project);


    // Get the Custom project UI config for the current project and see if the 'fileTransfer' feature is enabled for this project. If the
    // feature is enabled, check to see which FileRequestAction is enabled and create an Action object for each FileRequestAction enabled.
    const supportedActions = projectUIConfig?.enabledFeatures?.fileTransfer?.supportedActions;

    return [{
        label: capitalizeAll(trans('label.show_all')),
        value: '',
    }, ...([
        FileRequestAction.DOWNLOAD_FROM_DEVICE,
        FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER,
        FileRequestAction.DOWNLOAD_FROM_ROBOT,
        FileRequestAction.UPLOAD_TO_DEVICE,
        FileRequestAction.UPLOAD_TO_CONTROL_SERVER,
        FileRequestAction.UPLOAD_TO_ROBOT,
    ].filter((action) => {
        // filter out actions NOT supported for the selected project
        return supportedActions?.[action] !== undefined;
    }).map((actionType: FileRequestAction): SelectInputOption<FileRequestAction> => {
        return {
            label: capitalizeAll(trans(RCUtils.getFileRequestActionName(actionType))),
            value: actionType,
        };
    }))];
};



/**
 * Create an Array of file request status option used for filter by status dropdown
 * @param trans
 */
const createStatusFilterOptions = (trans: TFunction): readonly SelectInputOption<FileRequestStatus | ''>[] => {
    return [{
        label: capitalizeAll(trans('label.show_all')),
        value: '',
    }, ...(Object.values(FileRequestStatus).map((status: FileRequestStatus): SelectInputOption<FileRequestStatus> => {
        return {
            label: capitalizeAll(trans(RCUtils.getFileRequestStatusName(status))),
            value: status,
        };
    }))];
};



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface FileRequestsTableHiddenFields {
    readonly id?: boolean;
    readonly homeId?: boolean;
    readonly deviceId?: boolean;
    readonly robotId?: boolean;
    readonly robotClass?: boolean;
    readonly target?: boolean;
    readonly action?: boolean;
    readonly status?: boolean;
    readonly errorMessage?: boolean;
    readonly createdAt?: boolean;
    readonly updatedAt?: boolean;
    readonly expirationDate?: boolean;
}



interface RCFileRequestsListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly homeId: number;
    readonly deviceId: number;
    readonly robotId?: string | undefined;
    readonly userId?: number;
    readonly title?: string;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: FileRequestsTableHiddenFields;
    readonly readonly?: boolean;
    readonly filters?: FetchFileRequestFilters;
    readonly showFilterOptions?: boolean;
}


interface FileRequestsSearchFields {
    readonly target: FileRequestTarget | undefined;
    readonly action: FileRequestAction | undefined;
    readonly status: FileRequestStatus | undefined;
}



/**
 * This container display ALL the Project's File Requests. This container is just a wrapper for the RCFileRequestTable component. It contains
 * the logics for the RCFileRequestTable.
 */
export const RCFileRequestsListingContainer: React.FC<RCFileRequestsListingContainerProps> = (props: RCFileRequestsListingContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);
    const [selectedFileRequestId, setSelectedFileRequestId] = useState<number | undefined>();

    // Find the robot definition for the provided robotId in props. We need to check if the selected robot is a DIRECT robot
    // or GATEWAY robot so that we can disable the File Transfer feature for direct robots
    const selectedRobotDefinition = useSelectRobotDefinition(props.projectId, props.homeId, props.robotId);


    // Filtered by Target options
    const targetFilterOptions = useMemo(() => {
        return createFilterByTargetOptions(trans);
    }, [trans]);


    // Filtered by Action options
    const actionFilterOptions = useMemo(() => {
        return createFilterByActionOptions(trans, project, loggedInAgent);
    }, [loggedInAgent, project, trans]);


    // Filtered by Status options
    const statusFilterOptions = useMemo(() => {
        return createStatusFilterOptions(trans);
    }, [trans]);



    const {
        searchParams, onItemsPerPageChange, onOtherFieldsChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<FileRequestsSearchFields>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_100,
        currentPage : 0,
        otherFields : {
            target: props.filters?.target,
            action: props.filters?.action,
            status: props.filters?.status,
        },
    });



    // Select the list of file requests based on the current filters
    const fileRequests = selectAllFileRequests(rcDataState, props.homeId, {
        ...props.filters,
        deviceId  : props.deviceId,
        robotId   : props.robotId,
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
        target    : searchParams.otherFields.target,
        action    : searchParams.otherFields.action,
        status    : searchParams.otherFields.status,
    });



    /**
     * This useEffect is used for fetching robots details when we have new list of 'fileRequests'. We need to display some robot info e.g.
     * robotClass which is not available in fileRequest objects therefore we need to load robot data. However, we only need to load robots
     * info for the robots that we have not fetched yet
     */
    useEffect(() => {
        if (fileRequests && fileRequests.items.length > 0) {
            fileRequests.items.forEach((fileRequest) => {
                // if this file request is related to robot, fetch robot data if it is not already loaded
                if (fileRequest.robotId) {
                    // try to get robot object from the data state
                    const robot = selectRobotById(rcDataState, fileRequest.homeId, fileRequest.robotId);

                    // if robot object is not available, fetch it.
                    if (!robot) {
                        rcDataDispatch(fetchRobotById(props.projectId, fileRequest.homeId, fileRequest.robotId)).catch(() => {
                            // ignore error because dataDispatch already handled the error
                        });
                    }
                }
            });
        }
    }, [dataDispatch, fileRequests, props.projectId, rcDataDispatch, rcDataState]);



    // Handlers for target filter changed event
    const onFilterByTargetChange = useCallback((target: FileRequestTarget | '') => {
        if (target) {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.target = target;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.target = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    // Handlers for status filter changed event
    const onFilterByActionChange = useCallback((action: FileRequestAction | '') => {
        if (action !== '') {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.action = action;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.action = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    // Handlers for status filter changed event
    const onFilterByStatusChange = useCallback((status: FileRequestStatus | '') => {
        if (status !== '') {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.status = status;
            }));
        } else {
            onOtherFieldsChange(produce(searchParams.otherFields, (draft: Draft<FileRequestsSearchFields>) => {
                draft.status = undefined;
            }));
        }
    }, [onOtherFieldsChange, searchParams.otherFields]);



    /**
     * On create request action clicked, open the preview panel and use it to show the Create request form
     */
    const onCreateFileRequest = useCallback((action: FileRequestAction) => {

        // First, check to make sure there isn't already a request in PENDING status that has the SAME action, deviceId, and robotId.
        // If there is 1, don't allow the user to create a new request. If there is a PENDING request but the action, deviceId, robotId is not the
        // same then it is ok to create a new request.
        const pendingRequest = fileRequests?.items.find((request) => {
            return request.status === FileRequestStatus.PENDING && request.action === action
                && request.deviceId === props.deviceId && request.robotId === props.robotId;
        });

        if (!pendingRequest) {
            setSelectedFileRequestId(undefined);

            const options: RCFileRequestPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.FILE_REQUEST_PREVIEW,
                compProps: {
                    data: {
                        projectId          : props.projectId,
                        homeId             : props.homeId,
                        deviceId           : props.deviceId,
                        robotId            : props.robotId,
                        selectedFileRequest: action,
                    },
                    context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId, robotId: props.robotId },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        } else {
            // Show an error message and make it 10 secs long since it is a long message.
            uiDispatch(showErrorNotification('file_transfer.notif.error.blocking_create_file_request', undefined, Time.SECOND_IN_MS * 10));
        }
    }, [fileRequests, props.deviceId, props.homeId, props.projectId, props.robotId, props.userId, uiDispatch]);



    // Create the filter settings to be passed to the FileRequestsTableComp
    const fileRequestsFilterSettings: RCFileRequestsTableFilters = useMemo(() => {
        return {
            target: {
                currentValue: searchParams.otherFields.target,
                label       : capitalizeAll(trans('file_transfer.filter_option_label.target')),
                options     : targetFilterOptions,
                onChange    : onFilterByTargetChange,
            },
            action: {
                currentValue: searchParams.otherFields.action,
                label       : capitalizeAll(trans('file_transfer.filter_option_label.action')),
                options     : actionFilterOptions,
                onChange    : onFilterByActionChange,
            },
            status: {
                currentValue: searchParams.otherFields.status,
                label       : capitalizeAll(trans('file_transfer.filter_option_label.status')),
                options     : statusFilterOptions,
                onChange    : onFilterByStatusChange,
            },
        };
    }, [searchParams.otherFields.target, searchParams.otherFields.action, searchParams.otherFields.status, trans, targetFilterOptions,
        onFilterByTargetChange, actionFilterOptions, onFilterByActionChange, statusFilterOptions, onFilterByStatusChange]);



    /**
     * Create the set of actions for create requests
     */
    const fileRequestTableCustomActions = useMemo((): readonly BaseCompAction[] => {
        const projectUIConfig = ProjectTypeHandlersManager.getInstance().getProjectCustomUIHandler(
            project?.supportToolSettings.projectType,
        )?.getCustomUIConfig(loggedInAgent, project);


        // Get the Custom project UI config for the current project and see if the 'fileTransfer' feature is enabled for this project. If the
        // feature is enabled, check to see which FileRequestAction is enabled and create an Action object for each FileRequestAction enabled.
        const supportedActions = projectUIConfig?.enabledFeatures?.fileTransfer?.supportedActions;

        const actions: BaseCompAction[] = [];
        if (supportedActions) {
            if (supportedActions[FileRequestAction.DOWNLOAD_FROM_DEVICE] && props.robotId === undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.download_from_device')),
                    icon   : 'get_app',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.DOWNLOAD_FROM_DEVICE);
                    },
                });
            }
            if (supportedActions[FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER] && props.robotId === undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.download_from_control_server')),
                    icon   : 'get_app',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.DOWNLOAD_FROM_CONTROL_SERVER);
                    },
                });
            }
            if (supportedActions[FileRequestAction.DOWNLOAD_FROM_ROBOT] && props.robotId !== undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.download_from_robot')),
                    icon   : 'get_app',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.DOWNLOAD_FROM_ROBOT);
                    },
                });
            }
            if (supportedActions[FileRequestAction.UPLOAD_TO_DEVICE] && props.robotId === undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.upload_to_device')),
                    icon   : 'upload',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.UPLOAD_TO_DEVICE);
                    },
                });
            }
            if (supportedActions[FileRequestAction.UPLOAD_TO_CONTROL_SERVER] && props.robotId === undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.upload_to_control_server')),
                    icon   : 'upload',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.UPLOAD_TO_CONTROL_SERVER);
                    },
                });
            }
            if (supportedActions[FileRequestAction.UPLOAD_TO_ROBOT] && props.robotId !== undefined) {
                actions.push({
                    label  : capitalizeAll(trans('file_transfer.label.upload_to_robot')),
                    icon   : 'upload',
                    onClick: () => {
                        onCreateFileRequest(FileRequestAction.UPLOAD_TO_ROBOT);
                    },
                });
            }
        }
        return actions;

    }, [loggedInAgent, onCreateFileRequest, project, props.robotId, trans]);



    /**
     * Fetch requests base on the current pagination params
     */
    const fetchMoreFileRequests = useCallback(async () => {
        rcDataDispatch(fetchAllFileRequests(
            props.homeId, {
                ...props.filters,
                deviceId  : props.deviceId,
                robotId   : props.robotId,
                pageNumber: searchParams.currentPage,
                pageSize  : searchParams.itemsPerPage,
                target    : searchParams.otherFields.target,
                action    : searchParams.otherFields.action,
                status    : searchParams.otherFields.status,
            },
        )).catch(() => {
            // ignore error because dataDispatch already handled the error
        });
    }, [rcDataDispatch, props.homeId, props.filters, props.deviceId, props.robotId, searchParams.currentPage, searchParams.itemsPerPage,
        searchParams.otherFields.target, searchParams.otherFields.action, searchParams.otherFields.status]);



    /**
     * This use effect is used for fetching more file requests when the itemsPerPage or currentPage from the table pagination changed.
     */
    useEffect(() => {
        fetchMoreFileRequests();
    }, [dataDispatch, fetchMoreFileRequests, props.deviceId, props.homeId, rcDataDispatch]);



    /**
     * This used effect is used for loading file requests on component loads or when file requests are cleared
     */
    useEffect(() => {
        if (fileRequests === undefined) {
            fetchMoreFileRequests();
        }
    }, [fetchMoreFileRequests, fileRequests]);



    /**
     * Localize the file requests data before passing it to the FileRequestsTableComp for display
     */
    const localizedFileRequestsData = useMemo((): readonly RCFileRequestsTableDataItem[] | undefined => {

        // This is the list of statuses that indicate the request IS COMPLETED and the status is FINAL and will not change anymore.
        // If a request has one of these statuses, we will NOT allow the user to reload the request because there is no point of reloading it.
        const notReloadableStatuses = [
            FileRequestStatus.CANCELED,
            FileRequestStatus.FAILED,
            FileRequestStatus.SUCCEEDED,
            FileRequestStatus.TIME_OUT,
            FileRequestStatus.WARNING,
            FileRequestStatus.NOT_FOUND,
        ];


        if (fileRequests && fileRequests.items.length > 0) {
            return fileRequests.items.map((fileRequest: FileRequest): RCFileRequestsTableDataItem => {

                const isDownloadRequest = isDownloadFileRequest(fileRequest);
                const isSucceededDownloadRequest = isDownloadRequest && fileRequest.status === FileRequestStatus.SUCCEEDED;

                const userAction = ((): FileRequestUserAction | undefined => {

                    if (isSucceededDownloadRequest) {
                        return FileRequestUserAction.DOWNLOAD;
                    }
                    if (!notReloadableStatuses.includes(fileRequest.status)) {
                        return FileRequestUserAction.RELOAD_STATUS;
                    }

                    return undefined;
                })();

                // Get the robot from the data state. NOTE: this can be undefined if robot data is not fetched yet
                const robot = selectRobotById(rcDataState, fileRequest.homeId, fileRequest.robotId);

                return {
                    actualValue : fileRequest,
                    displayValue: {
                        id            : fileRequest.id.toString(),
                        homeId        : fileRequest.homeId.toString(),
                        deviceId      : fileRequest.deviceId?.toString() || '',
                        robotId       : fileRequest.robotId || '',
                        robotClass    : robot?.robotClass || '',
                        status        : capitalizeAll(trans(RCUtils.getFileRequestStatusName(fileRequest.status))),
                        target        : capitalizeAll(trans(RCUtils.getFileRequestTargetName(fileRequest.target))),
                        requestAction : capitalizeAll(trans(RCUtils.getFileRequestActionName(fileRequest.action))),
                        createdAt     : getDateTimeAndElapsedTime(fileRequest.createdAt, trans, false),
                        updatedAt     : getDateTimeAndElapsedTime(fileRequest.updatedAt, trans, false),
                        expirationDate: getDateTimeAndElapsedTime(fileRequest.expirationDate, trans, false),
                    },
                    selected: selectedFileRequestId === fileRequest.id,
                    action  : userAction,
                };
            });
        }

        return undefined;
    }, [fileRequests, rcDataState, trans, selectedFileRequestId]);



    // The table settings that we need to pass to the FileRequestsTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const fileRequestsTableSettings = useMemo((): RCFileRequestsTableProps['fieldsSettings'] => {
        return {
            fields: {
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    hidden      : props.hiddenFields?.id,
                },
                homeId: {
                    dataItemProp: 'homeId',
                    label       : capitalizeAll(trans('label.home_id')),
                    hidden      : props.hiddenFields?.homeId,
                },
                deviceId: {
                    dataItemProp: 'deviceId',
                    label       : capitalizeAll(trans('label.device_id')),
                    hidden      : props.hiddenFields?.deviceId,
                },
                robotId: {
                    dataItemProp: 'robotId',
                    label       : capitalizeAll(trans('label.robot_id')),
                    hidden      : props.hiddenFields?.robotId,
                },
                robotClass: {
                    dataItemProp: 'robotClass',
                    label       : capitalizeAll(trans('label.robot_class')),
                    hidden      : props.hiddenFields?.robotClass,
                },
                target: {
                    dataItemProp: 'target',
                    label       : capitalizeAll(trans('file_transfer.label.target')),
                    hidden      : props.hiddenFields?.target,
                },
                requestAction: {
                    dataItemProp: 'action',
                    label       : capitalizeAll(trans('file_transfer.label.action')),
                    hidden      : props.hiddenFields?.action,
                },
                errorMessage: {
                    dataItemProp: 'errorMessage',
                    label       : capitalizeAll(trans('file_transfer.label.error_message')),
                    hidden      : props.hiddenFields?.errorMessage,
                },
                createdAt: {
                    dataItemProp: 'createdAt',
                    label       : capitalizeAll(trans('label.date_created')),
                    hidden      : props.hiddenFields?.createdAt,
                },
                updatedAt: {
                    dataItemProp: 'updatedAt',
                    label       : capitalizeAll(trans('label.date_updated')),
                    hidden      : props.hiddenFields?.updatedAt,
                },
                expirationDate: {
                    dataItemProp: 'expirationDate',
                    label       : capitalizeAll(trans('file_transfer.label.expiration_date')),
                    hidden      : props.hiddenFields?.expirationDate,
                },
                status: {
                    dataItemProp: 'status',
                    label       : capitalizeAll(trans('label.status')),
                    hidden      : props.hiddenFields?.status,
                },
                action: {
                    label: '',
                },
            },
        };
    }, [trans, props.hiddenFields]);



    // The table pagination settings that we need to pass to the FileRequestsTableComp. We will create and cache it instead of
    // creating it each time we re-render this container.
    const fileRequestsTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : fileRequests && fileRequests.range ? fileRequests.range.total : undefined,
                hasMoreItems    : (fileRequests && fileRequests.range
                    ? fileRequests.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!fileRequests || fileRequests.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, fileRequests, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    /**
     * On fileRequest selected, open preview panel for the selected file request
     */
    const onFileRequestSelected = useCallback((fileRequest: FileRequest) => {
        setSelectedFileRequestId(fileRequest.id);

        const options: RCFileRequestPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.FILE_REQUEST_PREVIEW,
            compProps: {
                data: {
                    projectId          : props.projectId,
                    homeId             : props.homeId,
                    deviceId           : props.deviceId,
                    robotId            : props.robotId,
                    selectedFileRequest: fileRequest.id,
                },
                context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId, robotId: props.robotId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.deviceId, props.homeId, props.projectId, props.robotId, props.userId, uiDispatch]);



    /**
     * On reload file request button clicked, make a call to fetch the selected file request
     */
    const onReloadFileRequestStatus = useCallback(async (fileRequest: FileRequest) => {
        rcDataDispatch(fetchFileRequestById(fileRequest.homeId, fileRequest.id)).catch(() => {
            // ignore error
        });
    }, [rcDataDispatch]);



    /**
     * On Download button clicked, make an API call to get the download URL and then open it in a new tab.
     * But before that, need to make sure the file request is a type of DOWNLOAD device/robot and the request is SUCCESS status and not expired
     */
    const onDownloadFileRequestFile = useCallback(async (fileRequest: FileRequest) => {
        if (isDownloadFileRequest(fileRequest) && fileRequest.status === FileRequestStatus.SUCCEEDED) {
            RobotCloudAPI.getInstance().getDownloadFileRequestDownloadURL(fileRequest.homeId, fileRequest.id).then((url: string) => {
                window.open(url, 'blank');
            }).catch((error) => {
                uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
            });
        }
    }, [uiDispatch]);



    return (
        <>
            {/*
                Only show the FileRequestsTable if there is no robotId (this container is used for displaying Gateway's file requests)
                or if there is a robotId AND the robotDefinition for the specified robotId has "gateway" value for modeCommunicationType
            */}
            {project && (!props.robotId || (props.robotId && selectedRobotDefinition?.modeCommunicationType === RobotCommunicationType.GATEWAY)) && (
                <RCFileRequestsTable
                    className={clsx(props.className)}
                    listData={localizedFileRequestsData}
                    title={props.title || capitalizeAll(trans('label.file_request', { count: 2 }))}
                    icon="fas fa-file-alt"
                    onListItemSelected={onFileRequestSelected}
                    onReloadFileRequestStatus={onReloadFileRequestStatus}
                    onDownloadFileRequestFile={onDownloadFileRequestFile}
                    filters={fileRequestsFilterSettings}
                    paginationComp={fileRequestsTablePaginationBar}
                    fieldsSettings={fileRequestsTableSettings}
                    customSecondaryActions={fileRequestTableCustomActions}
                    texts={{
                        reloadRequestStatusTooltip: capitalizeFirst(trans('file_transfer.label.reload_file_request')),
                        downloadRequestTooltip    : capitalizeFirst(trans('file_transfer.label.download_file_request')),
                    }}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.file_request', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.file_request', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}

            {/*
                If there is a robotId and the robotDefinition for the selected robot DOES NOT have value 'gateway' for modeCommunicationType
                then show "Feature not supported" message
            */}
            {project && props.robotId && selectedRobotDefinition?.modeCommunicationType !== RobotCommunicationType.GATEWAY && (
                <GenericMessageBox
                    title={trans('generic.feature_not_supported.title', {
                        term: capitalizeAll(trans('file_transfer.feature_name')),
                    })}
                />
            )}

            {!project && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('RCFileRequestsContainer', RCFileRequestsListingContainer);
